//@flow
import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import getter from 'dibs-object-getter';
import classnames from 'classnames';

import { logError } from '../../helpers';
import { profileMediaMutation } from '../mutations/profileMedia';
import { upload } from '../helpers/imageUploader';

import styles from './ProfileIcon.scss';

import { Spinner } from 'dibs-elements/exports/Spinner';

import type { DropzoneFile } from 'react-dropzone';
import type { RelayProp } from 'react-relay';
import type { ProfileIcon_tradeFirm as TradeFirmType } from './__generated__/ProfileIcon_tradeFirm.graphql';

type Props = {
    isEditMode: boolean,
    tradeFirm: ?TradeFirmType,
    relay?: RelayProp,
};

type State = {
    isSaving: boolean,
    hasError: boolean,
    profilePath: ?string,
};

type IconOverlayProps = {
    overlayWrapperClasses: string,
    isEditMode: boolean,
    isSaving: boolean,
    hasError: boolean,
};

const IconOverlay = ({
    overlayWrapperClasses,
    isEditMode,
    isSaving,
    hasError,
}: IconOverlayProps) => {
    if (!isEditMode) {
        return null;
    } else if (isSaving) {
        return (
            <div className={styles.isSaving}>
                <Spinner size="small" />
            </div>
        );
    } else if (hasError) {
        return (
            <div className={overlayWrapperClasses}>
                <FormattedMessage
                    id="abt.designProfile.header.profileIcon.error"
                    defaultMessage="350px x 350px JPEG"
                />
            </div>
        );
    }
    return (
        <div className={overlayWrapperClasses}>
            <FormattedMessage
                id="abt.designProfile.header.profileIcon.upload"
                defaultMessage="Upload your profile photo"
            />
        </div>
    );
};

export class ProfileIcon extends Component<Props, State> {
    static getProfilePath(props: Props): ?string {
        return getter(props.tradeFirm)
            .at('publicProfile')
            .value('profilePhoto');
    }

    constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            hasError: false,
            profilePath: '',
        };
    }

    static getDerivedStateFromProps(nextProps: Props) {
        const profilePath = ProfileIcon.getProfilePath(nextProps);
        return { profilePath };
    }

    onDrop = (acceptedFiles: Array<DropzoneFile> = []) => {
        this.uploadImage(acceptedFiles[0]);
    };

    uploadImage = (file?: DropzoneFile) => {
        this.setState({ isSaving: true }, () => {
            const firmId = getter(this.props.tradeFirm).value('serviceId');
            if (!file || !firmId) {
                this.setState({ hasError: true, isSaving: false });
                return;
            }

            upload(file, firmId)
                .then(result => {
                    if (result.hasError) {
                        this.setState({
                            hasError: true,
                            isSaving: false,
                            profilePath: ProfileIcon.getProfilePath(this.props),
                        });
                        return { hasError: true, loggedErrorAlready: true };
                    }

                    // update the preview after save, since it'll be disposed
                    const profilePath = getter(result)
                        .at('image')
                        .value('path');

                    this.setState({ profilePath });

                    const profilePhotoId = getter(result)
                        .at('image')
                        .value('id');

                    const splashPhotoId =
                        getter(this.props.tradeFirm)
                            .at('publicProfile')
                            .value('splashPhotoId') || '';

                    if (!this.props.relay) {
                        throw new Error('No relay prop found for mutation');
                    }

                    if (!profilePhotoId) {
                        throw new Error('No new ID returned for the profile photo');
                    }

                    return profileMediaMutation(this.props.relay.environment, {
                        serviceId: firmId + '',
                        profilePhotoId: profilePhotoId + '',
                        splashPhotoId: splashPhotoId + '',
                    });
                })
                .then(profileResult => {
                    if (profileResult && profileResult.loggedErrorAlready) {
                        return;
                    } else if (profileResult && profileResult.hasError) {
                        this.setState({
                            hasError: true,
                            isSaving: false,
                            profilePath: ProfileIcon.getProfilePath(this.props),
                        });
                        return;
                    }

                    this.setState({ hasError: false, isSaving: false });
                })
                .catch(e => {
                    logError(e);
                    this.setState({
                        hasError: true,
                        isSaving: false,
                        profilePath: ProfileIcon.getProfilePath(this.props),
                    });
                });
        });
    };

    render() {
        const name = getter(this.props.tradeFirm).value('name');
        let profilePhoto =
            (this.state.isSaving
                ? this.state.profilePath
                : ProfileIcon.getProfilePath(this.props)) || '';

        if (profilePhoto && profilePhoto.length) {
            profilePhoto = `${profilePhoto}?width=300`; // displays nicer on hidpi screens
        }

        if (this.props.isEditMode && !this.state.isSaving) {
            const overlayWrapper = classnames(styles.overlay, {
                [styles.isEditMode]: this.props.isEditMode && !profilePhoto,
            });
            const profilePhotoStyle = {
                background: `url("${profilePhoto}")`,
                backgroundSize: 'cover',
            };
            const wrapper = classnames(styles.profilePhotoEdit, {
                [styles.editMode]: this.props.isEditMode,
                [styles.hasError]: this.state.hasError,
            });
            const icon = (
                <div className={wrapper} style={profilePhotoStyle}>
                    {
                        <IconOverlay
                            overlayWrapperClasses={overlayWrapper}
                            isEditMode={this.props.isEditMode}
                            isSaving={this.state.isSaving}
                            hasError={this.state.hasError}
                        />
                    }
                </div>
            );
            return (
                <Dropzone accept="image/jpeg" multiple={false} onDrop={this.onDrop}>
                    {dropzoneProps => {
                        return (
                            <div {...dropzoneProps.getRootProps()} className={styles.wrapper}>
                                <input
                                    {...dropzoneProps.getInputProps()}
                                    data-tn="upload-profile-icon-input"
                                />
                                {icon}
                            </div>
                        );
                    }}
                </Dropzone>
            );
        }
        // read-only mode
        return (
            <div className={styles.profilePhotoWrapper}>
                <div className={styles.profilePhotoFrame}>
                    <img src={profilePhoto} className={styles.profilePhoto} alt={name} />;
                </div>
            </div>
        );
    }
}

export const wrapped = createFragmentContainer(ProfileIcon, {
    tradeFirm: graphql`
        fragment ProfileIcon_tradeFirm on TradeFirmType {
            serviceId
            name
            publicProfile {
                profilePhoto
                splashPhotoId
            }
        }
    `,
});
